import {
  FormContext,
  FormInputGroup,
  FormInputGroupLayout,
  FormProps,
  FormState,
  FormValues,
  getValue,
  InputForm,
  InputFormComponent,
  InputFormGroup,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import {
  DefaultButton,
  FocusZone,
  FocusZoneTabbableElements,
  IButtonProps,
  IButtonStyles,
  mergeStyleSets,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { RegionSelect } from "../../components/RegionSelect/RegionSelect";
import { SubscriptionSelect } from "../../components/SubscriptionSelect/SubscriptionSelect";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext, SettingType } from "../../console/SettingsContext";
import { DetailsPanelId, InlineFormId, PageId } from "../../constants/pluginConstants";
import { footerHeight } from "../../constants/uiConstants";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useSubscriptions } from "../../hooks/useSubscriptions";

export const enum Fields {
  DefaultSubscription = "defaultAzureSubscriptionId",
  Subscription = "subscription",
  Region = "region",
  }

export enum ButtonTestIds {
  ApplyButton = "apply-subscritpions-settings",
  DiscardButton = "discard-subscriptions-settings",
}

export enum FieldTestIds {
  SubscriptionsDropdownTestId = "setting-subscription-select"
}

const groupNameFormatter = (index: number): string => `${Fields.Subscription}-${Fields.Region}-${index}`;
const subscriptionFieldNameFormatter = (index: number): string => `${Fields.Subscription}-${index}`;
const locationFieldNameFormatter = (index: number): string => `${Fields.Region}-${index}`;

const classNames = mergeStyleSets({
  subscriptionSelect: {
    minWidth: "107px",
    marginLeft: "0.5rem",
    marginBottom: "2rem",
    maxWidth: "26rem",
  },
  subscription: {
    width: "calc(100% - 1rem)",
    gridColumn: "1 / 2",
    minWidth: "107px",
    maxWidth: "404px",
  },
  regionSelect: {
    width: "calc(100% - 1rem)",
    gridColumn: "2 / 3",
    minWidth: "107px",
    maxWidth: "404px",
  },
  actionFooter: {
    position: "fixed",
    padding: "10px 10px 0px 10px",
    bottom: `${footerHeight}px`,
    width: "100%",
    height: "44px",
  },
  buttons: {
    justifyContent: "flex-start",
    marginTop: 3,
  },
});

const btnStyles: IButtonStyles = { root: { height: 24 } };

interface ResetFormButtonProps extends IButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultFormValues: { field: string; group?: string; value: any; }[];
}

const ResetFormButton: React.FC<ResetFormButtonProps> = ({
  defaultFormValues,
  onClick,
  children,
  ...rest
}): JSX.Element => {
  const form = React.useContext<FormState>(FormContext);

  if (!Object.keys(form).length) {
    throw new Error("ResetFormButton should be used within form");
  }

  const internalOnClick: ResetFormButtonProps["onClick"] = (event): void => {
    defaultFormValues.forEach(({ field, group, value }) => {
      form.setValue(value, field, group);
    });
    onClick?.(event);
  };

  return (
    <DefaultButton
      styles={btnStyles}
      onClick={internalOnClick}
      {...rest}
    >
      {children}
    </DefaultButton>
  );
};

export const SettingSubscriptionsLocations = (): JSX.Element => {
  const { trackFormSave } = useAnalytics();

  const {
    preferredSubscription,
    preferredLocationPerSubscription,
    setSettingValue,
  } = React.useContext<Settings>(SettingsContext);
  const filterState = React.useContext<FilterState>(FilterStateContext);

  const formComponentRef = React.useRef<InputFormComponent>();
  const setFormComponentRef = (formComponent: InputFormComponent): void => {
    formComponentRef.current = formComponent;
  };
  const [key, setKey] = React.useState(uniqueGUID());
  const { subscriptions } = useSubscriptions();
  const [areActionsDisabled, setActionsDisabled] = React.useState<boolean>(true);

  const defaultFormValues: ResetFormButtonProps["defaultFormValues"] = subscriptions?.map((subscription, index) => ({
    group: groupNameFormatter(index),
    field: locationFieldNameFormatter(index),
    value: preferredLocationPerSubscription[subscription.id]
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ? [preferredLocationPerSubscription[subscription.id]]!
      : undefined,
  })) ?? [];

  const defaultSubscription = subscriptions?.find(({ id }) => id === preferredSubscription);

  defaultFormValues.push({
    group: InputFormGroup,
    field: Fields.DefaultSubscription,
    value: defaultSubscription ? [{ id: defaultSubscription.id, text: defaultSubscription.name }] : undefined,
  });

  const rerenderForm = (): void => setKey(uniqueGUID());

  React.useEffect(() => {
    rerenderForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultFormValues)]);

  const onValueUpdate: FormProps["onValueUpdate"] = (formValues: FormValues): void => {
    let formValuesModified = false;

    const selectedPreferredSubscription = getValue(formValues, Fields.DefaultSubscription, InputFormGroup)?.[0]?.id;

    if (selectedPreferredSubscription !== preferredSubscription) {
      formValuesModified = true;
    }

    Object.keys(formValues).forEach(group => {
      if (group !== InputFormGroup) {
        const fields = Object.keys(formValues[group]);

        let region: string | undefined;

        fields.forEach(field => {
          if (field.startsWith(Fields.Region)) {
            region = getValue(formValues, field, group)?.[0]?.id;
          }
        });
        const defaultRegion = defaultFormValues.find(
          formVals => formVals.group === group && formVals.field.startsWith(Fields.Region),
        )?.value[0];

        formValuesModified = formValuesModified
          || region !== defaultRegion;
      }
    });
    setActionsDisabled(!formValuesModified);
  };

  const onClick = (): void => {
    if (!formComponentRef.current?.isValidationEnabled()) {
      formComponentRef.current?.enableValidation();
    }
    formComponentRef.current?.submit();
  };

  const onSubmit = (formValues: FormValues): void => {
    const defaultSubId = getValue(formValues, Fields.DefaultSubscription, InputFormGroup)[0].id;

    const formGroups = Object.keys(formValues).filter(group => group !== InputFormGroup);

    const newLocationsPerSubscription: Settings["preferredLocationPerSubscription"] = {};

    formGroups.forEach((group, index) => {
      const subscriptionName = getValue(formValues, subscriptionFieldNameFormatter(index), group);
      const subscriptionId = subscriptions?.find(sub => sub.name === subscriptionName)?.id;
      const locationId = getValue(formValues, locationFieldNameFormatter(index), group)[0].id;

      if (subscriptionId) {
        newLocationsPerSubscription[subscriptionId] = locationId;
      }
    });

    setSettingValue(SettingType.PREFERRED_SUBSCRIPTION, defaultSubId);
    setSettingValue(SettingType.PREFERRED_LOCATION_PER_SUBSCRIPTION, newLocationsPerSubscription);
    filterState.setDefaultSubscription(defaultSubId);
    filterState.setDefaultLocation(newLocationsPerSubscription[defaultSubId]);
  };

  const formInputGroups = subscriptions?.map((subscription, index) => (
    <FormInputGroup
      // eslint-disable-next-line react/no-array-index-key
      key={subscription.id + index}
      style={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "15rem 12rem",
        gridTemplateRows: "100%",
        justifyItems: "center",
        alignContent: "center",
      }}
      layout={FormInputGroupLayout.COMPACT}
      groupName={groupNameFormatter(index)}
    >
      <Stack className={classNames.subscription}>
        <TextInput
          disabled
          defaultValue={subscription.name}
          // When this is not included and the page is refreshed
          // the subscription.id is shown instead
          key={subscription.name}
          label={index === 0 ? Messages.labels.subscription() : undefined}
          fieldName={subscriptionFieldNameFormatter(index)}
        />
      </Stack>
      <Stack className={classNames.regionSelect}>
        <RegionSelect
          subscriptionId={subscription.id}
          fieldName={locationFieldNameFormatter(index)}
          tooltip={Messages.hints.toolTipRegion()}
          label={index === 0 ? Messages.labels.defaultLocation() : undefined}
          defaultValue={
            preferredLocationPerSubscription[subscription.id]
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              ? [preferredLocationPerSubscription[subscription.id]!]
              : undefined
          }
        />
      </Stack>
    </FormInputGroup>
  ));

  return (
    <InputForm
      key={key}
      onFormChange={onValueUpdate}
      componentRef={setFormComponentRef}
      onSubmit={(formValues: FormValues) => {
        onSubmit(formValues);
      }}
    >
      <Stack className={classNames.subscriptionSelect}>
        <SubscriptionSelect
          testId={FieldTestIds.SubscriptionsDropdownTestId}
          fieldName={Fields.DefaultSubscription}
          label={Messages.labels.defaultSubscription()}
          defaultValue={preferredSubscription ? [preferredSubscription] : undefined}
        />
      </Stack>
      {formInputGroups}
      <footer className={classNames.actionFooter}>
        <FocusZone handleTabKey={FocusZoneTabbableElements.all} isCircularNavigation>
          <Stack className={classNames.buttons} tokens={{ childrenGap: 8 }} horizontal>
            <PrimaryButton
              disabled={areActionsDisabled}
              onClick={() => {
                trackFormSave(InlineFormId.SETTING_SUBSCRIPTIONS, PageId.SETTINGS, DetailsPanelId.SUBSCRIPTIONS);
                onClick();
              }}
              styles={btnStyles}
              data-test-id={ButtonTestIds.ApplyButton}
            >
              {Messages.actions.apply()}
            </PrimaryButton>
            <ResetFormButton
              styles={btnStyles}
              disabled={areActionsDisabled}
              onClick={() => {
                rerenderForm();
              }}
              defaultFormValues={defaultFormValues}
              data-test-id={ButtonTestIds.DiscardButton}
            >
              {Messages.actions.discardChanges()}
            </ResetFormButton>
          </Stack>
        </FocusZone>
      </footer>
    </InputForm>
  );
};
